import React, {ReactNode, useEffect} from 'react';
import ImportExportIcon from '@material-ui/icons/ImportExport';
import ArrowUpwardIcon from '@material-ui/icons/ArrowUpward';
import ArrowDownwardIcon from '@material-ui/icons/ArrowDownward';
import TableCell from '@material-ui/core/TableCell';
import {
  Box,
  Button,
  Checkbox,
  CircularProgress,
  FormControl,
  InputBase,
  InputLabel,
  ListItemText,
  MenuItem,
  Select,
  Table,
  TableFooter,
  TablePagination,
} from '@material-ui/core';
import {Skeleton} from '@material-ui/lab';
import RefreshIcon from '@material-ui/icons/Refresh';
import Paper from '@material-ui/core/Paper';
import SearchIcon from '@material-ui/icons/Search';
import TableContainer from '@material-ui/core/TableContainer';
import TableHead from '@material-ui/core/TableHead';
import TableRow from '@material-ui/core/TableRow';
import TableBody from '@material-ui/core/TableBody';
import TablePaginationActions from '@material-ui/core/TablePagination/TablePaginationActions';

export type HeaderConfig = {
  field: string;
  title: string;
  isSortable?: boolean;
  sortField?: string;
  isSearchable?: boolean;
  searchField?: string;
  isRightAligned?: boolean;
  isCenterAligned?: boolean;
  maxWidth?: number;
  noWrap?: boolean;
  hidden?: boolean;
};

export type Row = {
  id: string | number;
  [field: string]: string | number | Date | undefined | ReactNode | any;
};

export type Filter = {
  field: string;
  value: any;
  title: string;
  parentField?: string;
  parentValue?: any;
};

export type FilterGroup = {
  name: string;
  filters: Filter[];
  dependsOn?: string;
};
export type ActiveFilterState = {filterGroupId: string; filterIndex: number};

type FilterToApply = Filter & {
  filterGroupId: string;
};

const DEFAULT_ROWS_PER_PAGE_OPTIONS: (
  | number
  | {label: string; value: number}
)[] = [5, 10, 20, 50, {label: 'All', value: -1}];

export function KhulaSupplierTable(props: {
  headers: HeaderConfig[];
  rows: Row[];
  defaultSortField?: string;
  defaultSortIsAscending?: boolean;
  filtersGroups: FilterGroup[];
  defaultActiveFilters?: ActiveFilterState[];
  isLoading: boolean;
  isRefreshing: boolean;
  onRefresh: () => any;
  onRowSelect: (row: Row) => any;
  defaultRowsPerPage?: number;
  rowsPerPageOptions?: (number | {label: string; value: number})[];
  onRowsPerPageChange?: (value: number) => void;
  toolbarActions?: ReactNode;
}) {
  const {headers, rows, filtersGroups, isLoading} = props;
  const rowsPerPageOptions =
    props.rowsPerPageOptions || DEFAULT_ROWS_PER_PAGE_OPTIONS;

  const [page, setPage] = React.useState(0);
  const [rowsPerPage, setRowsPerPage] = React.useState(
    props.defaultRowsPerPage ?? 10,
  );
  const [searchTerm, setSearchTerm] = React.useState('');
  const [sortField, setSortField] = React.useState(props.defaultSortField);
  const [sortIsAscending, setSortIsAscending] = React.useState(
    props.defaultSortIsAscending !== false,
  );
  const [activeFilters, setActiveFilters] = React.useState<ActiveFilterState[]>(
    props.defaultActiveFilters || [],
  );
  const defaultActiveFilters = props.defaultActiveFilters || [];

  useEffect(() => {
    setActiveFilters(props.defaultActiveFilters || []);
  }, [props.defaultActiveFilters]);

  useEffect(() => {
    if (props.defaultRowsPerPage === undefined) return;
    setRowsPerPage(props.defaultRowsPerPage);
    setPage(0);
  }, [props.defaultRowsPerPage]);

  const handleChangePage = (event: any, newPage: number) => {
    setPage(newPage);
  };

  const handleChangeRowsPerPage = (event: any) => {
    const value = parseInt(event.target.value, 10);
    setRowsPerPage(value);
    setPage(0);
    props.onRowsPerPageChange?.(value);
  };

  const handleSearchUpdate = (searchTerm: string) => {
    setSearchTerm(searchTerm);
    setPage(0);
  };

  const handleSortUpdate = (newSortField: string) => {
    const newSortIsAscending =
      newSortField === sortField ? !sortIsAscending : false;

    setSortField(newSortField);
    setSortIsAscending(newSortIsAscending);
    setPage(0);
  };

  const handleFiltersUpdate = (
    filterGroupId: string,
    activeIndexes: number[],
  ) => {
    const dependentFilterGroupIds = filtersGroups
      .filter((filterGroup) => filterGroup.dependsOn === filterGroupId)
      .map((filterGroup) => filterGroup.name);
    const otherActiveFiltersGroups = activeFilters.filter(
      (filter) =>
        filter.filterGroupId !== filterGroupId &&
        !dependentFilterGroupIds.includes(filter.filterGroupId),
    );
    const currActiveFilterGroup: ActiveFilterState[] = activeIndexes.map(
      (filterIndex) => ({
        filterGroupId,
        filterIndex,
      }),
    );
    setActiveFilters([...otherActiveFiltersGroups, ...currActiveFilterGroup]);
    setPage(0);
  };

  const handleClearFilters = () => {
    setSearchTerm('');
    setActiveFilters(defaultActiveFilters);
    setPage(0);
  };

  const getAllActiveFilters = (): FilterToApply[] => {
    return activeFilters
      .map((state) => {
        const filterGroup = filtersGroups.find(
          (filterGroup) => filterGroup.name === state.filterGroupId,
        );
        if (!filterGroup) return undefined;
        return {
          filterGroupId: filterGroup!.name,
          ...filterGroup.filters[state.filterIndex],
        };
      })
      .filter((filter) => !!filter) as FilterToApply[];
  };

  const getFilterOptionsForGroup = (filterGroup: FilterGroup) => {
    const filterOptions = filterGroup.filters.map((filter, index) => ({
      filter,
      index,
    }));
    if (!filterGroup.dependsOn) return filterOptions;

    const parentFilters = getAllActiveFilters().filter(
      (filter) => filter.filterGroupId === filterGroup.dependsOn,
    );
    if (!parentFilters.length) return [];

    return filterOptions.filter(({filter}) =>
      parentFilters.some(
        (parentFilter) =>
          filter.parentField === parentFilter.field &&
          filter.parentValue === parentFilter.value,
      ),
    );
  };

  const getActiveFilterIndexesForFilterGroup = (
    filterGroupId: string,
  ): number[] => {
    return activeFilters
      .filter((state) => state.filterGroupId === filterGroupId)
      .map((state) => state.filterIndex);
  };

  const getSortIcon = (field: string) => {
    if (sortField !== field) return <ImportExportIcon />;
    else if (sortIsAscending) return <ArrowUpwardIcon />;
    else return <ArrowDownwardIcon />;
  };

  const filteredRows = [rows]
    .map((rows) => searchRows(headers, rows, searchTerm))
    .map((rows) => filterRows(rows, getAllActiveFilters()))
    .map((rows) => sortRows(rows, sortField, sortIsAscending))[0];

  const activeFiltersAreDefault =
    activeFilters.length === defaultActiveFilters.length &&
    activeFilters.every((activeFilter) =>
      defaultActiveFilters.some(
        (defaultFilter) =>
          defaultFilter.filterGroupId === activeFilter.filterGroupId &&
          defaultFilter.filterIndex === activeFilter.filterIndex,
      ),
    );
  const hasFiltersToClear = !!searchTerm || !activeFiltersAreDefault;

  if (isLoading)
    return (
      <Box className="khula-supplier-table-container">
        <Skeleton
          variant="rect"
          height={40}
          style={{borderRadius: 8}}
          className="khula-supplier-table-header-container"
        />
        <Box mb={1} />
        <Skeleton variant="rect" height={600} style={{borderRadius: 8}} />
      </Box>
    );

  return (
    <Box className="khula-supplier-table-container">
      <Box className="khula-supplier-table-header-container">
        <Paper
          className="khula-supplier-table-search-container"
          variant="outlined"
        >
          <SearchIcon className="khula-supplier-table-search-icon" />
          <InputBase
            className="khula-supplier-table-search-input"
            placeholder="Search"
            autoFocus
            value={searchTerm}
            onChange={(event) => handleSearchUpdate(event.target.value)}
          />
        </Paper>
        {filtersGroups?.map((filterGroup) => {
          const filterOptions = getFilterOptionsForGroup(filterGroup);
          if (!filterOptions.length) return null;

          const activeOriginalIndexes = getActiveFilterIndexesForFilterGroup(
            filterGroup.name,
          );
          const activeFilterIndexes = activeOriginalIndexes
            .map((activeIndex) =>
              filterOptions.findIndex(({index}) => index === activeIndex),
            )
            .filter((index) => index >= 0);

          return (
            <FilterSelect
              key={filterGroup.name}
              name={filterGroup.name}
              filters={filterOptions.map(({filter}) => filter)}
              activeFilterIndexes={activeFilterIndexes}
              onActiveFilterIndexesChange={(activeFilterIndices) =>
                handleFiltersUpdate(
                  filterGroup.name,
                  activeFilterIndices
                    .map((filterIndex) => filterOptions[filterIndex]?.index)
                    .filter(
                      (filterIndex): filterIndex is number =>
                        filterIndex !== undefined,
                    ),
                )
              }
            />
          );
        })}
        <Box className="khula-supplier-table-actions">
          {hasFiltersToClear && (
            <Button
              className="clear-filters-button"
              size="small"
              onClick={handleClearFilters}
            >
              Clear filters
            </Button>
          )}
          {props.toolbarActions}
          <Button
            variant="outlined"
            size="small"
            className="refresh-button"
            onClick={() => props.onRefresh()}
            title="Refresh"
          >
            {props.isRefreshing ? (
              <CircularProgress size={20} thickness={6} />
            ) : (
              <RefreshIcon />
            )}
          </Button>
        </Box>
      </Box>
      <TableContainer
        component={(props) => (
          <Paper {...props} variant="outlined" square={false} />
        )}
      >
        <Table className="khula-supplier-table" size="small">
          <TableHead>
            <TableRow>
              {headers
                .filter((header) => !header.hidden)
                .map((header) =>
                  header.isSortable ? (
                    <SortableTableCell
                      key={header.field}
                      title={header.title}
                      sortField={header.field}
                      alignment={
                        header.isCenterAligned
                          ? 'center'
                          : header.isRightAligned
                          ? 'right'
                          : 'left'
                      }
                      icon={getSortIcon(header.sortField || header.field)}
                      onToggle={() =>
                        handleSortUpdate(header.sortField || header.field)
                      }
                    />
                  ) : (
                    <TableCell
                      key={header.field}
                      align={
                        header.isCenterAligned
                          ? 'center'
                          : header.isRightAligned
                          ? 'right'
                          : 'left'
                      }
                    >
                      {header.title}
                    </TableCell>
                  ),
                )}
            </TableRow>
          </TableHead>
          <TableBody>
            {(rowsPerPage > 0
              ? filteredRows.slice(
                  page * rowsPerPage,
                  page * rowsPerPage + rowsPerPage,
                )
              : filteredRows
            ).map((row) => (
              <TableRow
                key={(row.idFormatted as string) || row.id}
                className="khula-supplier-table-row"
              >
                {headers
                  .filter((header) => !header.hidden)
                  .map((header) => (
                    <TableCell
                      key={header.field}
                      align={header.isRightAligned ? 'right' : 'left'}
                      style={{
                        ...(header.maxWidth && {maxWidth: header.maxWidth}),
                        ...(header.noWrap && {whiteSpace: 'nowrap'}),
                      }}
                      onClick={() =>
                        // ignores on click event for react node cells
                        !React.isValidElement(row[header.field])
                          ? props.onRowSelect(row)
                          : null
                      }
                    >
                      {row[header.field] as any}
                    </TableCell>
                  ))}
              </TableRow>
            ))}
          </TableBody>
          <TableFooter>
            <TableRow>
              <TablePagination
                rowsPerPageOptions={rowsPerPageOptions}
                colSpan={9}
                count={filteredRows.length}
                rowsPerPage={rowsPerPage}
                page={page}
                SelectProps={{
                  inputProps: {'aria-label': 'rows per page'},
                  native: true,
                }}
                onChangePage={handleChangePage}
                onChangeRowsPerPage={handleChangeRowsPerPage}
                ActionsComponent={TablePaginationActions}
              />
            </TableRow>
          </TableFooter>
        </Table>
      </TableContainer>
    </Box>
  );
}

const searchRows = (
  headers: HeaderConfig[],
  rows: Row[],
  searchTerm: string,
) => {
  if (!searchTerm) return rows;
  searchTerm = searchTerm.toLowerCase();

  const searchFields = headers
    .filter((header) => header.isSearchable)
    .map((header) => header.searchField || header.field);

  const rowIsEligible = (row: Row, searchTerm: string) => {
    return searchFields.some((field) =>
      row[field]?.toString().toLowerCase().includes(searchTerm),
    );
  };

  return rows.filter((row) => rowIsEligible(row, searchTerm));
};

const filterRows = (rows: Row[], activeFilters: FilterToApply[]) => {
  if (!activeFilters?.length) return rows;

  const rowIsEligible = (row: Row, activeFilters: FilterToApply[]) => {
    const groupIds = activeFilters.map((filter) => filter.filterGroupId);
    const uniqueGroupIds = Array.from(new Set(groupIds));

    // row must match every active filter group
    return uniqueGroupIds.every((groupId) => {
      return activeFilters
        .filter((filter) => filter.filterGroupId === groupId)
        .some((filter) => row[filter.field] == filter.value);
    });
  };

  return rows.filter((row) => rowIsEligible(row, activeFilters));
};

const sortRows = (rows: Row[], field?: string, isAsc?: boolean) => {
  if (!field) return rows;
  if (!rows?.length) return rows;

  // ignore trying to sort react nodes
  if (React.isValidElement(rows[0][field])) return rows;

  const compare = (a: any, b: any) => {
    if (a[field] < b[field]) return -1;
    if (a[field] > b[field]) return 1;
    return 0;
  };

  let sorted = rows.sort(compare);
  if (!isAsc) sorted = sorted.reverse();
  return sorted;
};

function FilterSelect(props: {
  name: string;
  filters: Filter[];
  activeFilterIndexes: number[];
  onActiveFilterIndexesChange: (activeFilterIndices: number[]) => any;
}) {
  const labelId = `${props.name
    .replace(/\s+/g, '-')
    .toLowerCase()}-filter-label`;

  const getSelectedLabel = (selected: unknown) => {
    const selectedIndexes = selected as number[];
    const selectedTitles = selectedIndexes
      .map((index) => props.filters[index]?.title)
      .filter(Boolean);

    if (!selectedTitles.length) return props.name;
    if (selectedTitles.length <= 2) return selectedTitles.join(', ');

    return `${selectedTitles.length} selected`;
  };

  return (
    <FormControl className="select-filter" size="small" variant="outlined">
      <InputLabel id={labelId}>{props.name}</InputLabel>
      <Select
        labelId={labelId}
        id={labelId.replace('-label', '')}
        multiple
        label={props.name}
        value={props.activeFilterIndexes}
        renderValue={getSelectedLabel}
        MenuProps={{
          PaperProps: {
            className: 'select-filter-menu',
          },
        }}
        onChange={(event) => {
          const activeFilterIndexes = event.target.value as number[];
          props.onActiveFilterIndexesChange(activeFilterIndexes);
        }}
      >
        {props.filters.map((filter, index) => (
          <MenuItem key={index} value={index}>
            <Checkbox
              checked={props.activeFilterIndexes.includes(index)}
              className="select-filter-checkbox"
              color="default"
              disableRipple
            />
            <ListItemText primary={filter.title} />
          </MenuItem>
        ))}
      </Select>
    </FormControl>
  );
}

const SortableTableCell = (props: {
  title: string;
  sortField: string;
  alignment?: 'left' | 'right' | 'center';
  icon: ReactNode;
  onToggle: () => any;
}) => {
  return (
    <TableCell onClick={props.onToggle} align={props.alignment}>
      <Button
        size="small"
        className="khula-supplier-table-col-heading"
        endIcon={props.icon}
        style={{justifyContent: props.alignment}}
      >
        {props.title}
      </Button>
    </TableCell>
  );
};
